import React from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, AlertTriangle } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";

function Field({ label, value, valueClass = "text-gray-900" }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1">
            <dt className="text-[11px] text-gray-500">{label}</dt>
            <dd
                className={`text-xs font-mono tabular-nums truncate ${valueClass}`}
            >
                {value}
            </dd>
        </div>
    );
}

function verdictColor(v) {
    if (v === "blocked") return "text-emerald-600";
    if (v === "open") return "text-red-600";
    return "text-amber-600";
}

export default function ApiSecurityDetails({ result, func, onFixInChat }) {
    const { t } = useTranslation();
    if (!result || result.state !== "done") return null;
    const staticResult = result.staticResult || {};
    const live = result.live || {};

    const warnings = (live.warnings || []).map((code) =>
        code === "wrappedStatus"
            ? t("appPreview.warningWrappedStatus") ||
              "Returns HTTP 200 with a 4xx body. HTTP-aware clients treat this as success."
            : code
    );

    return (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
            {warnings.length > 0 && (
                <div className="flex gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-px" />
                    <div>
                        <div className="font-medium mb-0.5">
                            {t("appPreview.warningsHeading") || "Warnings"}
                        </div>
                        <div>{warnings.join(" ")}</div>
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                <section>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1.5">
                        {t("appPreview.staticCheckHeading") ||
                            "Static check (AST)"}
                    </h4>
                    <dl>
                        <Field
                            label={t("appPreview.fieldPassed") || "Passed"}
                            value={String(staticResult.passed ?? "—")}
                            valueClass={
                                staticResult.passed
                                    ? "text-emerald-600"
                                    : "text-red-600"
                            }
                        />
                        <Field
                            label={t("appPreview.fieldReason") || "Reason"}
                            value={staticResult.reason || "—"}
                        />
                    </dl>
                </section>
                <section>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1.5">
                        {t("appPreview.liveCheckHeading") || "Live probe"}
                    </h4>
                    <dl>
                        <Field
                            label={t("appPreview.fieldVerdict") || "Verdict"}
                            value={live.verdict || "—"}
                            valueClass={verdictColor(live.verdict)}
                        />
                        {live.status !== undefined && (
                            <Field
                                label={t("appPreview.fieldStatus") || "Status"}
                                value={String(live.status)}
                            />
                        )}
                        {live.reason && (
                            <Field
                                label={t("appPreview.fieldReason") || "Reason"}
                                value={live.reason}
                            />
                        )}
                    </dl>
                </section>
            </div>

            {live.snippet && (
                <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1.5">
                        {t("appPreview.responseSnippet") || "Response"}
                    </h4>
                    <pre className="text-[11px] text-gray-700 font-mono bg-white border border-gray-200 rounded-md p-3 max-h-40 overflow-auto whitespace-pre-wrap leading-relaxed">
                        {live.snippet}
                    </pre>
                </div>
            )}

            {!result.passed && (
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onFixInChat(func)}
                        className="h-8 px-3 text-xs"
                    >
                        <MessageCircle className="w-3 h-3 mr-1.5" />
                        {t("appPreview.fixInChat") || "Fix in chat"}
                    </Button>
                </div>
            )}
        </div>
    );
}
